/**
 * Chat prose → file-reference token splitter (workspace-file-tags; analysis §7/§8).
 * Masks code/quoted regions (verbatim), then extracts `@tag` references, then the
 * bare §1.5 machine-name safety net over the remaining literal text — into a token
 * array the chat renderer maps to <FileChip> (tokens) + plain text (strings).
 *
 * The tokenizer stays "dumb": it only LOCATES references. Resolution (a registered
 * `@tag` → chip, an unknown one → literal text) is the chip's job via the tag index
 * — closed-world, graceful degradation §8 (a broken tag never errors, just renders
 * as its literal `@text`).
 *
 * Assistant blocks only (the caller gates it) — user text renders verbatim. Code
 * spans / fenced blocks are never tokenized: the escape rule (§6.4, `` `@tag` ``)
 * and the evidence guard (§6.3, an agent's fenced transcript quote must keep its
 * `@`-strings verbatim).
 */

import {
  codeRegionRe,
  machineNameRe,
  tagProseRe,
  TAG_TIP_RE,
} from "./filetag-grammar";

export interface FileRefToken {
  kind: "fileref";
  /** The `@tag` BODY (no `@`), or a bare machine name. The resolver maps this to a
   *  file entry; an unknown tag renders verbatim (`@` + body), never an error. */
  payload: string;
  /** Provenance — an `@tag` reference or a bare machine name. */
  source: "tag" | "bare";
  /** Optional `#v<N>` version pin (Stage B — carried, not yet resolved). */
  version?: string;
}

export type FileRefSegment = string | FileRefToken;

export interface SplitFileRefOptions {
  /** Mid-stream: a trailing partial `@word` (or a lone `@`) at the very tail is HELD
   *  (dropped from output) so a tag still being typed doesn't flash as literal then
   *  re-render as a chip. Off (final message) → it tokenizes normally. */
  streaming?: boolean;
}

const isToken = (s: FileRefSegment): s is FileRefToken => typeof s !== "string";

/**
 * Where a streaming render should truncate to hold in-progress content at the tail,
 * or -1 for none. Two cases, earliest wins:
 *  A. an UNTERMINATED fenced block (an odd count of ```) — hold from its opening
 *     ``` so a still-arriving quoted-evidence block never chip-ifies its `@`-strings
 *     before the fence closes (evidence guard §6.3);
 *  B. a trailing `@` at a word boundary, optionally followed by a partial body /
 *     `#v`, at end-of-text (a tag still being typed). `email@` never holds.
 */
export function streamingHoldStart(text: string): number {
  const fences = text.match(/```/g);
  if (fences && fences.length % 2 === 1) {
    return text.lastIndexOf("```"); // Case A — unterminated fence
  }
  const m = TAG_TIP_RE.exec(text);
  return m ? m.index : -1; // Case B — in-progress @tag
}

/** Split a LITERAL run on the bare machine-name safety net into text + bare tokens. */
function splitMachineNames(literal: string): FileRefSegment[] {
  if (literal === "") return [];
  const out: FileRefSegment[] = [];
  const re = machineNameRe();
  let last = 0;
  for (let m = re.exec(literal); m !== null; m = re.exec(literal)) {
    if (m.index > last) out.push(literal.slice(last, m.index));
    out.push({ kind: "fileref", payload: m[0], source: "bare" });
    last = m.index + m[0].length;
  }
  if (last < literal.length) out.push(literal.slice(last));
  return out;
}

/** Tokenize a NON-CODE run: `@tag` pass first, machine-name pass over the gaps. */
function tokenizeAuthored(text: string): FileRefSegment[] {
  const out: FileRefSegment[] = [];
  const re = tagProseRe();
  let last = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) out.push(...splitMachineNames(text.slice(last, m.index)));
    const body = m[1]!;
    const version = m[2];
    out.push(
      version
        ? { kind: "fileref", payload: body, source: "tag", version }
        : { kind: "fileref", payload: body, source: "tag" },
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...splitMachineNames(text.slice(last)));
  return out;
}

/**
 * Tokenize assistant chat prose into text + file-reference tokens. Code/quoted
 * regions pass through verbatim; each non-code run gets the `@tag` then machine-name
 * passes. Adjacent literal strings are coalesced; empty strings are dropped.
 */
export function splitFileRefTokens(
  input: string,
  opts?: SplitFileRefOptions,
): FileRefSegment[] {
  let text = input;
  if (opts?.streaming) {
    const hold = streamingHoldStart(text);
    if (hold >= 0) text = text.slice(0, hold);
  }

  // Mask code/quoted regions first — their contents are verbatim (never a chip).
  const raw: FileRefSegment[] = [];
  const cre = codeRegionRe();
  let last = 0;
  for (let m = cre.exec(text); m !== null; m = cre.exec(text)) {
    if (m.index > last) raw.push(...tokenizeAuthored(text.slice(last, m.index)));
    raw.push(m[0]); // the code region, verbatim literal
    last = m.index + m[0].length;
  }
  if (last < text.length) raw.push(...tokenizeAuthored(text.slice(last)));

  // Coalesce adjacent literals, drop empties.
  const out: FileRefSegment[] = [];
  for (const seg of raw) {
    if (!isToken(seg)) {
      if (seg === "") continue;
      const prev = out[out.length - 1];
      if (prev !== undefined && !isToken(prev)) {
        out[out.length - 1] = prev + seg;
        continue;
      }
    }
    out.push(seg);
  }
  return out;
}

/** True when a tokenized result contains at least one file-reference token —
 *  lets the renderer keep its fast path (plain text) when there's nothing to chip. */
export function hasFileRef(segments: readonly FileRefSegment[]): boolean {
  return segments.some(isToken);
}
